import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsTag } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsTag } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchLmsTag } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width} from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';

const LmsTag = () => {

    const { showToaster } = useToaster();
    const [selectedTag, setSelectedTag] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { lmsSubject, lmsTag } = useLmsDropdownOptions({ subjectFilter: { status: "active" } })

    const validationSchema = Yup.object().shape({
        subject_id: Yup.string().required('Subject is required'),
        tagName: Yup.string().required('TagName  is required'),

    });

    const initialValues = {
        subject_id: selectedTag?.subject_id || '',
        tagName: selectedTag?.tag || '',
    };

    const mutationAdd = useMutation({
        mutationFn: ({ formData, id }) => postLmsTag(id, formData),
        onSuccess: () => {
            showToaster('Tag added successfully!', '', 'success');
            queryClient.invalidateQueries(['tags/all']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to add tag', '', 'error');
        },
    });


    const mutationUpdate = useMutation({
        mutationFn: ({ formData, id }) => putLmsTag(formData, id),
        onSuccess: () => {
            showToaster('Tag updated successfully!', '', 'success');
            queryClient.invalidateQueries(['tags/all']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Tag', '', 'error');
        },
    });


    const handleSubmit = (values, { resetForm }) => {
        const { subject_id, ...payload } = values
        if (isEditing) {
            mutationUpdate.mutate({ formData: values, id: selectedTag.id, });
        } else {
            mutationAdd.mutate({ formData: payload, id: selectedSubject.id, });
            resetForm();
        }
        setIsEditing(false);
        setSelectedTag(null);
    };

    const toggleStatusMutation = useMutation({
        mutationFn: ({ formData, id }) => patchLmsTag(formData, id),
        onSuccess: (response) => {
            showToaster(`Tag ${response?.data?.status ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries(['tags/all']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update status', '', 'error');
        },
    });

    const tagColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'tag', headerName: 'Tag', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'subject_name', headerName: 'Subject', flex: 1, renderCell: (params) => <Typography>{params.row.subject.subject}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedTag(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status}
                    onCheck={() =>
                        toggleStatusMutation.mutate({
                            formData: { status: !params.row.status ? true : false },
                            id: params.row.id
                        })
                    }
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Tag' : 'Add Tag'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="subject_id"
                                        component={IdAutoCompleteField}
                                        options={lmsSubject.options}
                                        isLoading={lmsSubject.isLoading}
                                        label="Subject"
                                        isRequired
                                        onValueChange={(val) => {
                                            setSelectedSubject(val)
                                        }}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field name="tagName"
                                        component={TextFormField}
                                        label="Tag"
                                        isRequired
                                    />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3}}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedTag(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3}}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>
            <CustomDivider />
            <TableFilterTemplate
                title="Tag List"
                columns={tagColumns}
                rowsData={lmsTag.options || []}
                isLoading={lmsTag.isLoading} />
        </Box>
    );
};

export default LmsTag;



